/* JC-EVO 09/26/2026: article design C "In this article" contents for sections/main-blog-post-sidebar.liquid (Eric's
   pick; plan memory/plans/fast-article-c.md). The section prints the empty contents shell when the article has 3+ H2s;
   this fills it from the rendered headings:
   - H2s = `.bsl__content h2` with text and a rendered box; fewer than 3 -> the <nav> is removed (the rail's share
     icons stay).
   - Anchors: an authored H2 id is kept as is; a missing id becomes the slug of the heading text (a-z, 0-9, "-",
     80 chars max), numbered -2, -3... when the document already has that id. Links are plain #id anchors, so the
     browser (and a crawler rendering the page) jumps natively; CSS scroll-margin-top keeps the heading clear of the
     sticky header, and reduced motion turns the smooth scroll off.
   - Current section: the last H2 above header bottom + 30% of the viewport gets aria-current="true" (none before the
     first H2); the desktop rail keeps that link in view when its list scrolls.
   - Below 1280px the list is a collapsible row: the button toggles aria-expanded; choosing a link closes it before the
     jump (so the target position is measured after the row shrinks); Escape closes it and returns focus to the button.
   Without JavaScript the shell is not shown (@media (scripting: none) in assets/jc-article.css).
   Also: a table wider than the reading column gets .jc-table-scroll (scrolls sideways) from 750px up. */
(() => {
  const slug = (text) => {
    let s = text.normalize('NFKD').replace(/[̀-ͯ]/g, '').toLowerCase()
      .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
    if (s.length > 80) s = s.slice(0, 80).replace(/-[^-]*$/, '');
    return s || 'section';
  };

  function init(root) {
    const nav = root.querySelector('[data-jc-toc]');
    if (!nav || nav.hasAttribute('data-jc-ready')) return;
    nav.setAttribute('data-jc-ready', '');
    const list = nav.querySelector('ol');
    const btn = nav.querySelector('button');
    const heads = [...root.querySelectorAll('.bsl__content h2')].filter((h) => h.textContent.trim() && h.getClientRects().length);
    if (!list || heads.length < 3) { nav.remove(); return; }

    const made = new Set();
    heads.forEach((h) => {
      if (h.id) return;
      const base = slug(h.textContent.trim());
      let id = base;
      for (let n = 2; document.getElementById(id); n++) id = `${base}-${n}`;
      h.id = id;
      made.add(h);
    });
    const links = heads.map((h) => {
      const li = document.createElement('li');
      const a = document.createElement('a');
      a.href = `#${encodeURIComponent(h.id)}`;
      a.textContent = h.textContent.replace(/\s+/g, ' ').trim();
      li.append(a);
      list.append(li);
      return a;
    });
    nav.classList.add('is-ready');

    // collapsible row (the button is only displayed below 1280px)
    const collapsible = () => btn && getComputedStyle(btn).display !== 'none';
    const setOpen = (open) => {
      if (!btn) return;
      btn.setAttribute('aria-expanded', String(open));
      nav.toggleAttribute('data-open', open);
    };
    if (btn) btn.addEventListener('click', () => setOpen(btn.getAttribute('aria-expanded') !== 'true'));
    list.addEventListener('click', (e) => { if (e.target.closest('a') && collapsible()) setOpen(false); });
    nav.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && btn && btn.getAttribute('aria-expanded') === 'true') { setOpen(false); btn.focus(); }
    });

    // current section
    const header = document.querySelector('#header-component');
    let cur = -2, queued = false;
    const spy = () => {
      queued = false;
      const top = header ? Math.max(0, header.getBoundingClientRect().bottom) : 0;
      const line = top + window.innerHeight * 0.3;
      let i = -1;
      heads.forEach((h, n) => { if (h.getBoundingClientRect().top <= line) i = n; });
      if (i === cur) return;
      cur = i;
      links.forEach((a, n) => (n === i ? a.setAttribute('aria-current', 'true') : a.removeAttribute('aria-current')));
      if (i >= 0 && !collapsible() && list.scrollHeight > list.clientHeight + 1) {
        const a = links[i].getBoundingClientRect(), l = list.getBoundingClientRect();
        if (a.top < l.top) list.scrollTop -= l.top - a.top + 8;
        else if (a.bottom > l.bottom) list.scrollTop += a.bottom - l.bottom + 8;
      }
    };
    const queue = () => { if (!queued) { queued = true; requestAnimationFrame(spy); } };
    window.addEventListener('scroll', queue, { passive: true });
    const pw = document.querySelector('.page-wrapper'); // the scroll container from 990px (Horizon base.css)
    if (pw) pw.addEventListener('scroll', queue, { passive: true });
    window.addEventListener('resize', queue);
    spy();

    // a link to a heading whose id was only just added: the browser could not jump to it while loading
    const target = location.hash ? document.getElementById(decodeURIComponent(location.hash.slice(1))) : null;
    if (target && made.has(target)) requestAnimationFrame(() => target.scrollIntoView());
  }

  // a table wider than the reading column scrolls sideways instead of widening the page (phones always do, in CSS)
  function fitTables(root) {
    const content = root.querySelector('.bsl__content');
    const tables = content ? [...content.querySelectorAll('table')] : [];
    if (!tables.length) return;
    let queued = false;
    const fit = () => {
      queued = false;
      tables.forEach((t) => {
        t.classList.remove('jc-table-scroll');
        if (t.getBoundingClientRect().width > content.clientWidth + 1) t.classList.add('jc-table-scroll');
      });
    };
    fit();
    window.addEventListener('resize', () => { if (!queued) { queued = true; requestAnimationFrame(fit); } });
  }

  const run = (scope) => scope.querySelectorAll('.jc-art').forEach((root) => { fitTables(root); init(root); });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => run(document));
  else run(document);
  document.addEventListener('shopify:section:load', (e) => run(e.target));
})();
